import { formateNumber, formateTimeSlot } from "./formating";
import { getNext7Days } from "./functions";

export function isPlaceholderAppointmentValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  return ["dummy", "test", "n/a", "na", "-", "tbd", "null", "undefined"].includes(
    normalized
  );
}

function firstMeaningful(...candidates) {
  return (
    candidates.find((item) => !isPlaceholderAppointmentValue(item)) || ""
  );
}

/** Dealer company name on office / appointment payloads (API may use several keys). */
export function getOfficeCompanyName(source) {
  if (!source || typeof source !== "object") {
    return "";
  }

  const dealerInfo =
    source.dealerInfo || source.dealer_info || source.DealerInfo || null;
  const dealerInfoIsObject =
    dealerInfo && typeof dealerInfo === "object" && !Array.isArray(dealerInfo);

  const dealer = source.dealer;
  const dealerIsObject =
    dealer && typeof dealer === "object" && !Array.isArray(dealer);

  return firstMeaningful(
    dealerInfoIsObject ? dealerInfo.companyName : null,
    dealerInfoIsObject ? dealerInfo.company_name : null,
    dealerInfoIsObject ? dealerInfo.CompanyName : null,
    dealerInfoIsObject ? dealerInfo.Company_name : null,
    source.company_name,
    source.Company_name,
    source.companyName,
    source.dealer_company_name,
    source.Dealer_company_name,
    source.dealer_name,
    source.Dealer_name,
    source.Company,
    source.company,
    dealerIsObject ? dealer.company_name : null,
    dealerIsObject ? dealer.Company_name : null,
    dealerIsObject ? dealer.companyName : null,
    source.user?.company_name,
    source.owner?.company_name,
    source.dealer_user?.company_name
  );
}

/** Dealer display name from nested dealerInfo when present. */
export function getOfficeDealerName(source) {
  if (!source || typeof source !== "object") {
    return "";
  }

  const dealerInfo =
    source.dealerInfo || source.dealer_info || source.DealerInfo || null;
  if (!dealerInfo || typeof dealerInfo !== "object") {
    return "";
  }

  return firstMeaningful(
    dealerInfo.dealerName,
    dealerInfo.dealer_name,
    dealerInfo.DealerName,
    dealerInfo.name
  );
}

/** Office.dealer is usually a dealer user id (integer). */
export function getOfficeDealerId(source) {
  if (!source || typeof source !== "object") {
    return null;
  }

  const dealerInfo =
    source.dealerInfo || source.dealer_info || source.DealerInfo || null;
  if (dealerInfo && typeof dealerInfo === "object" && !Array.isArray(dealerInfo)) {
    const nestedId = dealerInfo.id ?? dealerInfo.pk ?? dealerInfo.user_id;
    if (nestedId != null && nestedId !== "" && typeof nestedId !== "object") {
      return nestedId;
    }
  }

  const raw =
    source.dealer ??
    source.Dealer ??
    source.dealer_id ??
    source.Dealer_id ??
    source.dealerId;

  if (raw == null || raw === "") {
    return null;
  }

  if (typeof raw === "object") {
    const nested = raw.id ?? raw.pk ?? raw.user_id ?? raw.user;
    if (nested == null || nested === "" || typeof nested === "object") {
      return null;
    }
    return nested;
  }

  return raw;
}

export function getOfficeAddress(source) {
  if (!source || typeof source !== "object") {
    return "";
  }

  return firstMeaningful(
    source.Address,
    source.address,
    source.Area,
    source.area
  );
}

/** "Address - Company" when company is present; otherwise address only. */
export function formatOfficeLocationLabel(source) {
  const address = getOfficeAddress(source);
  const company = getOfficeCompanyName(source);

  if (address && company) {
    return `${address} - ${company}`;
  }

  return address || company || "";
}

export function getAppointmentLocation(appointment) {
  return formatOfficeLocationLabel(appointment);
}

export function getAppointmentSummaryParts(appointment) {
  if (!appointment) {
    return { location: "", date: "", time: "" };
  }

  const timeRaw = appointment.time_selected || "";
  const time = !isPlaceholderAppointmentValue(timeRaw)
    ? formateTimeSlot(timeRaw)
    : "";

  return {
    location: getAppointmentLocation(appointment),
    date:
      appointment.date_selected ||
      appointment.date_applied ||
      "",
    time: !isPlaceholderAppointmentValue(time) ? time : timeRaw,
  };
}

export function formatAppointmentPreview(appointment) {
  if (!appointment) {
    return "";
  }

  const parts = [
    getAppointmentLocation(appointment),
    appointment.date_selected || appointment.date_applied,
    appointment.time_selected ? formateTimeSlot(appointment.time_selected) : "",
  ].filter((part) => !isPlaceholderAppointmentValue(part));

  return parts.join(" · ");
}

export function normalizeAppointmentDate(value) {
  if (value == null || value === "") return "";

  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return `${parsed.getFullYear()}-${formateNumber(
    parsed.getMonth() + 1
  )}-${formateNumber(parsed.getDate())}`;
}

export function getAppointmentEvaluationId(appointment) {
  const raw =
    appointment?.evaluation ??
    appointment?.evaluation_id ??
    appointment?.Evaluation ??
    appointment?.evaluate_id;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    const nested = raw.id ?? raw.pk ?? raw.evaluation_id;
    return nested == null ? "" : String(nested);
  }
  return String(raw);
}

export function extractAppointmentRecord(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const found = extractAppointmentRecord(item);
      if (found) return found;
    }
    return null;
  }

  const id = payload.id ?? payload.appointment_id ?? payload.pk;
  if (id != null && id !== "") {
    return { ...payload, id };
  }

  if (payload.appointment) {
    const nested = extractAppointmentRecord(payload.appointment);
    if (nested) return nested;
  }
  if (payload.data && payload.data !== payload) {
    const nested = extractAppointmentRecord(payload.data);
    if (nested) return nested;
  }
  if (payload.results) {
    const nested = extractAppointmentRecord(payload.results);
    if (nested) return nested;
  }
  return null;
}

export function appointmentBelongsToEvaluation(appointment, evaluationId) {
  if (!appointment?.id) return false;
  if (!evaluationId) return true;
  const apptEval = getAppointmentEvaluationId(appointment);
  // Missing evaluation on the appointment must not match every vehicle
  // (stale Redux booking would mark unrelated cars as booked).
  if (!apptEval) return false;
  return String(apptEval) === String(evaluationId);
}

export function getBookedAppointmentRecord(evaluation, appointmentCreate) {
  const evaluationId = evaluation?.id;
  // Appointments already on this evaluation belong to it even without an
  // embedded evaluation id field.
  const fromEvaluation = Array.isArray(evaluation?.appointment)
    ? evaluation.appointment.find((item) => item?.id)
    : null;
  if (fromEvaluation?.id) {
    return fromEvaluation;
  }
  if (appointmentBelongsToEvaluation(appointmentCreate, evaluationId)) {
    return appointmentCreate;
  }
  return null;
}

/** Appointment.status values that lock client-side editing on Car Steps. */
const APPOINTMENT_EDIT_LOCKED_STATUSES = new Set([
  "pending",
  "approved",
  "completed",
]);

export function getAppointmentStatus(appointment) {
  if (!appointment || typeof appointment !== "object") return "";
  const raw =
    appointment.status ??
    appointment.Status ??
    appointment.appointment_status ??
    "";
  return String(raw).trim().toLowerCase();
}

/**
 * Booked appointments with no status default to "pending".
 * Returns "" when there is no appointment / booking signal.
 */
export function resolveAppointmentStatus(appointment) {
  if (!appointment || typeof appointment !== "object") return "";
  const status = getAppointmentStatus(appointment);
  if (status) return status;

  const { location, date, time } = getAppointmentSummaryParts(appointment);
  if (appointment.id || location || date || time) {
    return "pending";
  }
  return "";
}

/** CSS tone key for appointment status badges. */
export function getAppointmentStatusTone(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "none";
  if (normalized === "approved") return "approved";
  if (normalized === "completed" || normalized === "complete") return "completed";
  if (
    normalized === "cancelled" ||
    normalized === "canceled" ||
    normalized === "rejected" ||
    normalized === "declined"
  ) {
    return "cancelled";
  }
  if (normalized === "pending") return "pending";
  return "other";
}

/** Hide Edit when status is pending / approved / completed; show for other statuses. */
export function canEditBookedAppointment(appointment) {
  if (!appointment?.id) return false;
  const status = resolveAppointmentStatus(appointment);
  return !APPOINTMENT_EDIT_LOCKED_STATUSES.has(status);
}

export function formatAppointmentStatusLabel(appointment) {
  const status = resolveAppointmentStatus(appointment);
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Prefer API appointments; keep locally booked ones if the refetch is stale. */
export function mergeEvaluationAppointments(
  incomingEvaluation,
  existingEvaluation,
  appointmentCreate
) {
  const evaluationId =
    incomingEvaluation?.id ?? existingEvaluation?.id ?? null;
  const incoming = Array.isArray(incomingEvaluation?.appointment)
    ? incomingEvaluation.appointment.filter((item) => {
        if (!item?.id) return false;
        const apptEval = getAppointmentEvaluationId(item);
        if (!apptEval) return true;
        return !evaluationId || String(apptEval) === String(evaluationId);
      })
    : [];
  if (incoming.length) {
    return incoming;
  }
  const sameEvaluation =
    existingEvaluation?.id != null &&
    incomingEvaluation?.id != null &&
    String(existingEvaluation.id) === String(incomingEvaluation.id);
  const existing = sameEvaluation && Array.isArray(existingEvaluation?.appointment)
    ? existingEvaluation.appointment.filter((item) => item?.id)
    : [];
  if (existing.length) {
    return existing;
  }
  if (appointmentBelongsToEvaluation(appointmentCreate, evaluationId)) {
    return [appointmentCreate];
  }
  return Array.isArray(incomingEvaluation?.appointment)
    ? incomingEvaluation.appointment
    : [];
}

export function findOfficeForAppointment(officetiming, booked) {
  if (!booked || !Array.isArray(officetiming)) {
    return null;
  }

  const address = String(booked.Address || booked.address || "")
    .trim()
    .toLowerCase();
  const officeId = booked.office ?? booked.office_id ?? booked.Office;

  if (officeId != null && officeId !== "") {
    const byId = officetiming.find(
      (office) => String(office.id) === String(officeId)
    );
    if (byId) return byId;
  }

  if (address) {
    const byAddress = officetiming.find(
      (office) => String(office.address || "").trim().toLowerCase() === address
    );
    if (byAddress) return byAddress;
  }

  return null;
}

export function getOfficeTimmingMap(office) {
  if (!office || typeof office !== "object") {
    return {};
  }

  if (
    office.timming &&
    typeof office.timming === "object" &&
    !Array.isArray(office.timming)
  ) {
    return office.timming;
  }

  const list = Array.isArray(office.timings) ? office.timings : [];
  return list.reduce((map, item) => {
    if (!item || typeof item !== "object") return map;
    const index = item.Day ?? item.day ?? item.day_index;
    if (index == null || index === "") return map;
    const label = item.day_display || item.day || item.Day;
    map[index] = {
      ...item,
      day: label,
      start: item.Start_date || item.start,
      end: item.End_date || item.end,
      day_index: Number(index),
    };
    map[String(index)] = map[index];
    map[Number(index)] = map[index];
    return map;
  }, {});
}

export function buildSchedulerDays(office) {
  const days = getNext7Days();
  const timming = getOfficeTimmingMap(office);
  return days
    .map((day) => {
      const item = timming?.[day.id] ?? timming?.[String(day.id)];
      if (!item) return null;
      return {
        ...item,
        dateNum: day.dateNum,
        date: day.date,
        dayId: day.id,
      };
    })
    .filter(Boolean);
}

export function buildSelectionFromAppointment(office, booked) {
  const schedulerDays = buildSchedulerDays(office);
  if (!schedulerDays.length || !booked) {
    return { schedulerDays, selection: null, slot: "" };
  }

  const dateNum = normalizeAppointmentDate(
    booked.date_selected || booked.date_applied
  );
  const calendarDays = getNext7Days();
  const dayMeta =
    calendarDays.find((day) => day.dateNum === dateNum) || calendarDays[0];
  const timming = getOfficeTimmingMap(office);
  const timing = dayMeta
    ? timming?.[dayMeta.id] ?? timming?.[String(dayMeta.id)]
    : null;

  if (!timing) {
    return {
      schedulerDays,
      selection: null,
      slot: booked.time_selected || "",
    };
  }

  const matchedSchedulerDay =
    schedulerDays.find((day) => day.dayId === dayMeta.id) || schedulerDays[0];

  return {
    schedulerDays,
    selection: {
      ...timing,
      date: dateNum || matchedSchedulerDay?.dateNum || "",
      id: dayMeta.id,
      day_formated: booked.date_selected
        ? `${timing.day}, ${booked.date_selected}`
        : `${timing.day}, ${dayMeta.date}`,
    },
    slot: booked.time_selected || "",
  };
}

function parseSortTimestamp(value) {
  if (value == null || value === "") {
    return NaN;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && String(value).trim() !== "") {
    return asNumber;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? NaN : parsed;
}

function getVehicleAppointments(vehicle) {
  if (!vehicle?.appointment) {
    return [];
  }

  return Array.isArray(vehicle.appointment)
    ? vehicle.appointment
    : [vehicle.appointment];
}

/** Scheduled slot timestamp (date + time only — not booking created_at). */
export function getAppointmentScheduleTimestamp(appointment) {
  if (!appointment || typeof appointment !== "object") {
    return NaN;
  }

  const dateValue = normalizeAppointmentDate(
    appointment.date_selected || appointment.date_applied
  );

  if (!dateValue || isPlaceholderAppointmentValue(dateValue)) {
    return NaN;
  }

  const dateParts = dateValue.split("-").map(Number);
  if (dateParts.length !== 3 || dateParts.some((part) => Number.isNaN(part))) {
    return NaN;
  }

  const [year, month, day] = dateParts;
  const timeRaw = String(appointment.time_selected || "00:00:00").trim();
  const timeMatch = timeRaw.match(/^(\d{1,2}):(\d{2})/);
  const hours = timeMatch ? Number(timeMatch[1]) : 0;
  const minutes = timeMatch ? Number(timeMatch[2]) : 0;

  return new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
}

export function vehicleHasBookedAppointment(vehicle) {
  return getVehicleAppointments(vehicle).some(
    (appointment) => !Number.isNaN(getAppointmentScheduleTimestamp(appointment))
  );
}

function getVehicleScheduleTimestamp(vehicle) {
  const timestamps = getVehicleAppointments(vehicle)
    .map(getAppointmentScheduleTimestamp)
    .filter((timestamp) => !Number.isNaN(timestamp));

  if (!timestamps.length) {
    return NaN;
  }

  return Math.min(...timestamps);
}

export function sortAppointmentsBySchedule(appointments) {
  if (!Array.isArray(appointments) || appointments.length < 2) {
    return Array.isArray(appointments) ? [...appointments] : [];
  }

  return [...appointments].sort((left, right) => {
    const leftTs = getAppointmentScheduleTimestamp(left);
    const rightTs = getAppointmentScheduleTimestamp(right);

    if (Number.isNaN(leftTs) && Number.isNaN(rightTs)) {
      return 0;
    }
    if (Number.isNaN(leftTs)) {
      return 1;
    }
    if (Number.isNaN(rightTs)) {
      return -1;
    }

    if (leftTs !== rightTs) {
      return leftTs - rightTs;
    }

    const rightId = parseSortTimestamp(right?.id) || 0;
    const leftId = parseSortTimestamp(left?.id) || 0;
    return rightId - leftId;
  });
}

function compareAppointmentVehicles(left, right) {
  const leftBooked = vehicleHasBookedAppointment(left);
  const rightBooked = vehicleHasBookedAppointment(right);

  if (leftBooked !== rightBooked) {
    return leftBooked ? -1 : 1;
  }

  if (!leftBooked) {
    const rightId = parseSortTimestamp(right?.id) || 0;
    const leftId = parseSortTimestamp(left?.id) || 0;
    return rightId - leftId;
  }

  const leftTs = getVehicleScheduleTimestamp(left);
  const rightTs = getVehicleScheduleTimestamp(right);

  if (!Number.isNaN(leftTs) && !Number.isNaN(rightTs) && leftTs !== rightTs) {
    return leftTs - rightTs;
  }

  const rightId = parseSortTimestamp(right?.id) || 0;
  const leftId = parseSortTimestamp(left?.id) || 0;
  return rightId - leftId;
}

function normalizeVehicleAppointments(vehicle) {
  if (!Array.isArray(vehicle?.appointment) || vehicle.appointment.length < 2) {
    return vehicle;
  }

  return {
    ...vehicle,
    appointment: sortAppointmentsBySchedule(vehicle.appointment),
  };
}

/** Booked vehicles by soonest upcoming slot; unbooked vehicles last. */
export function sortAppointmentVehiclesForList(vehicles) {
  if (!Array.isArray(vehicles)) {
    return [];
  }

  if (vehicles.length < 2) {
    return vehicles.map(normalizeVehicleAppointments);
  }

  return [...vehicles]
    .sort(compareAppointmentVehicles)
    .map(normalizeVehicleAppointments);
}

/** @deprecated Use sortAppointmentVehiclesForList */
export const sortAppointmentVehiclesNewestFirst = sortAppointmentVehiclesForList;
